import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { fetchFundingRates } from '../lib/coinglass';
import { FundingRateSimple } from '../types/coinglass';

interface SimpleFundingRateProps {
  symbol?: string;
}

const SimpleFundingRate: React.FC<SimpleFundingRateProps> = ({
  symbol = 'BTC'
}) => {
  const { data, error, isLoading, refetch } = useQuery<FundingRateSimple[]>({
    queryKey: ['fundingRates', symbol],
    queryFn: async () => {
      const result = await fetchFundingRates(symbol);
      return result.sort((a: FundingRateSimple, b: FundingRateSimple) => Math.abs(b.fundingRate) - Math.abs(a.fundingRate));
    },
    refetchInterval: 5 * 60 * 1000, // 5 minutes
  });

  const getColorForRate = (rate: number) => {
    if (rate > 0) return 'text-green-500';
    if (rate < 0) return 'text-red-500';
    return 'text-gray-400';
  };

  if (isLoading || !data) return null;
  if (error) return null;

  // Get the highest absolute funding rate
  const highestRate = data[0]; // Already sorted by absolute value

  // Calculate some statistics
  const rates = data.map(item => item.fundingRate);
  const avgRate = rates.reduce((sum, rate) => sum + rate, 0) / rates.length;
  const positiveCount = rates.filter(rate => rate > 0).length;

  return (
    <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-800">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white">{symbol} Funding Rate</h3>
        <button
          onClick={() => refetch()}
          className="p-2 text-gray-400 hover:text-yellow-400 transition-colors"
          aria-label="Refresh"
        >
          <RefreshCw size={20} />
        </button>
      </div>
      
      {/* Highest Rate */}
      <div className="mb-4">
        <div className="text-sm text-gray-400 mb-1">Highest Rate</div>
        <div className={`text-3xl font-bold ${getColorForRate(highestRate.fundingRate)}`}>
          {(highestRate.fundingRate * 100).toFixed(4)}%
        </div>
        <div className="text-gray-400 text-sm">
          {highestRate.exchangeName}
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-2 gap-4 pt-4 border-t border-zinc-800">
        <div>
          <div className="text-xs text-gray-400 mb-1">Average</div>
          <div className={`text-lg font-semibold ${getColorForRate(avgRate)}`}>
            {(avgRate * 100).toFixed(4)}%
          </div>
        </div>
        <div>
          <div className="text-xs text-gray-400 mb-1">Sentiment</div>
          <div className="text-lg font-semibold text-white">
            {positiveCount > data.length / 2 ? 'Bullish' : 'Bearish'}
          </div>
        </div>
      </div>

      {/* Mini Chart/Bars */}
      <div className="mt-4">
        <div className="text-xs text-gray-400 mb-2">Rate Distribution</div>
        <div className="space-y-1">
          {data.slice(0, 5).map((item) => (
            <div key={item.exchangeName} className="flex items-center gap-2">
              <div className="w-16 text-xs text-gray-400 truncate">{item.exchangeName}</div>
              <div className="flex-1 bg-zinc-800 rounded-full h-2 relative overflow-hidden">
                <div 
                  className={`h-full rounded-full transition-all duration-500 ${
                    item.fundingRate > 0 ? 'bg-green-500' : 'bg-red-500'
                  }`}
                  style={{ 
                    width: `${Math.min(100, Math.abs(item.fundingRate) * 10000)}%` 
                  }}
                />
              </div>
              <div className={`text-xs font-mono ${getColorForRate(item.fundingRate)}`}>
                {(item.fundingRate * 100).toFixed(2)}%
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SimpleFundingRate;